import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { CampaignDto } from 'src/core/models/dtos/campaign.dto';
import getActiveWheelRequest from './getActiveWheel/getActiveWheel.repository';
import getCampaignPopRequest from './getCampaignPop/getCampaignPop.repository';
import getPackagesRequest from './getPackages/getPackages.repository';
import {
  getPackagesUpgradeRequest,
  putUpgradeSub,
} from './getPackagesUpgrade/getPackagesUpgrade.repository';
import getCampaignSubRequest from './getUserCampaignSub/getUserCampaignSub.repository';
import getUserInfoRequest from './getUserInfo/getUserInfo.repository';
import {
  putCampaignPopClickRequest,
  putCampaignPopSeenRequest,
} from './putCampaignPopSeen/putCampaignPopSeen.repository';

export const usePackagesUpgrade = (groupId?: number, enabled = true) => {
  return useQuery({
    queryKey: ['packagesUpgrade', groupId],
    queryFn: () => getPackagesUpgradeRequest(groupId),
    enabled,
  });
};

export const usePackages = (groupId?: number, enabled = true) => {
  return useQuery({
    queryKey: ['packages', groupId],
    queryFn: () => getPackagesRequest(groupId),
    enabled,
  });
};
export const useUpgradeSubscription = ({
  onSuccess,
}: {
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['upgradeSubscription'],
    mutationFn: ({
      fromUserSubId,
      toSubId,
    }: {
      fromUserSubId: number;
      toSubId: number;
    }) => putUpgradeSub({ fromUserSubId, toSubId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userInfo'] });
      onSuccess?.();
    },
  });
};

export const useUserInfo = (enabled = true) => {
  return useQuery({
    queryKey: ['userInfo'],
    queryFn: () => getUserInfoRequest(),
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
  });
};

export const useUserCampaignSub = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['userCampaignSub'],
    mutationFn: (code: string) => getCampaignSubRequest({ code }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userInfo'] });
    },
  });
};

export const useCampaignPop = (enabled = true) => {
  return useQuery({
    queryKey: ['campaignPop'],
    queryFn: () => getCampaignPopRequest(),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useCampaignPopSeen = () => {
  return useMutation({
    mutationKey: ['campaignPopSeen'],
    mutationFn: (campId: number | string) => putCampaignPopSeenRequest(campId),
  });
};

export const useCampaignPopClick = () => {
  return useMutation({
    mutationKey: ['campaignPopClick'],
    mutationFn: (campId: number | string) => putCampaignPopClickRequest(campId),
  });
};

export const useCampaignPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [campaignData, setCampaignData] = useState<CampaignDto | null>(null);

  const { data, error, isError } = useCampaignPop(true);

  useEffect(() => {
    if (data) {
      setCampaignData(data);
      setShowPopup(true);
    }
    if (isError && error) {
      setCampaignData(null);
      setShowPopup(false);
    }
  }, [data, error, isError]);

  const closePopup = () => {
    setShowPopup(false);
  };

  return {
    showPopup,
    campaignData,
    closePopup,
  };
};

export const useActiveWheel = () => {
  return useQuery({
    queryKey: ['activeWheel'],
    queryFn: () => getActiveWheelRequest(),
    retry: false,
    placeholderData: keepPreviousData,
  });
};
